import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { buildSignupUrl } from '../../lib/signupUrl';

const comparisonData = [
  { feature: 'Script from your content', adori: 'Automated', traditional: 'Write it yourself' },
  { feature: 'Professional voiceover', adori: '40+ AI voices', traditional: 'Record or hire' },
  { feature: 'Synced captions', adori: 'Auto-generated', traditional: 'Manual sync' },
  { feature: 'Relevant visuals', adori: 'AI-selected', traditional: 'Hours of searching' },
  { feature: 'YouTube SEO', adori: 'Built-in', traditional: 'Separate tool' },
  { feature: 'Tools required', adori: 'Just AdoriAI', traditional: '5–8 tools' },
  { feature: 'Time per video', adori: '~2 minutes', traditional: '4–8 hours' },
];

const ComparisonSection = () => {
  const signupUrl = buildSignupUrl();

  return (
    <section className="py-24 px-6" data-testid="comparison-section">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-accent mb-4 block">
            Why AdoriAI
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            Stop spending 6 hours
            <br />
            <span className="text-gray-400">making one video</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            The traditional way is a full-time job. AdoriAI is a 2-minute task.
          </p>
        </motion.div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Traditional */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-surface-elevated rounded-2xl p-8 border border-red-500/20 relative"
          >
            <div className="absolute -top-3 left-6">
              <span className="bg-surface-elevated px-4 py-1 text-sm font-semibold text-red-400 border border-red-500/20 rounded-full">
                Traditional
              </span>
            </div>
            
            <div className="mt-4 space-y-4">
              {comparisonData.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <X className="w-3.5 h-3.5 text-red-400" />
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">{item.feature}: </span>
                    <span className="text-gray-400">{item.traditional}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* AdoriAI */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="bg-surface rounded-2xl p-8 border border-brand-primary/30 relative glow-primary"
          >
            <div className="absolute -top-3 left-6">
              <span className="bg-brand-primary px-4 py-1 text-sm font-semibold text-surface-base rounded-full flex items-center gap-1">
                <span>✦</span> Adori AI
              </span>
            </div>
            
            <div className="mt-4 space-y-4">
              {comparisonData.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5 text-green-400" />
                  </div>
                  <div>
                    <span className="text-gray-500 text-sm">{item.feature}: </span>
                    <span className="text-white font-medium">{item.adori}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href={signupUrl}
            className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-surface-base px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 hover:-translate-y-1 glow-primary-strong"
            data-testid="comparison-cta"
          >
            Start free trial
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonSection;
